import fpFetch from "@/services/fpFetch";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { removeNodeHighlights } from "../common/utils/addOnServices/highlightTextNodes";
import {
  handleAltviaHighlighting,
  clearAltviaHighlights,
} from "./altviaHighlighter";
import { AltviaInteraction } from "./AltviaInteractionInterface";
import { Calendar, User, FileText, Star } from "lucide-react";

const AltviaInteractionViewer = ({ fileUrl }: { fileUrl: string }) => {
  const [interactionData, setInteractionData] =
    useState<AltviaInteraction | null>(null);
  const [formattedNotes, setFormattedNotes] = useState<string>("");
  const fileViewerData = useSelector((state: any) => state.fileViewerData);

  const formatNotes = (notes: string | undefined) => {
    if (!notes) return "";

    // Convert line breaks to HTML and preserve formatting
    return notes
      .replace(/\n/g, "<br>")
      .replace(/\r/g, "<br>")
      .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    if (interactionData?.notes) {
      setFormattedNotes(formatNotes(interactionData.notes));
    }
  }, [interactionData]);

  useEffect(() => {
    fpFetch(fileUrl)
      .then((res) => res.json())
      .then((data: AltviaInteraction) => {
        setInteractionData(data);
      })
      .catch(console.error);
  }, [fileUrl]);

  useEffect(() => {
    if (formattedNotes) {
      handleAltviaHighlighting(fileViewerData);
    }
  }, [formattedNotes, fileViewerData]);

  if (!interactionData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-gray-500">Loading Altvia interaction...</div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full bg-white overflow-auto p-8"
      onClick={() => {
        clearAltviaHighlights();
      }}
    >
      {/* Header Section */}
      <div className="mb-8">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {interactionData.subject ||
              interactionData.name ||
              "Untitled Interaction"}
          </h1>
          <p className="text-gray-600 mt-1">
            {interactionData.type || "No type specified"}
          </p>
        </div>
      </div>

      {/* Metadata Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-medium">
                {formatDate(interactionData.created_date)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Last Modified</p>
              <p className="font-medium">
                {formatDate(interactionData.last_modified_date)}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {interactionData.interaction_grades && (
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Grades</p>
                <p className="font-medium">
                  {interactionData.interaction_grades}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reference List */}
      {interactionData.reference_list && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <User className="w-5 h-5 text-gray-500" />
            Reference List
          </h3>
          <div className="altvia-reference bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">
              {interactionData.reference_list}
            </p>
          </div>
        </div>
      )}

      {/* Notes - Main Content */}
      {interactionData.notes && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            Notes
          </h3>
          <div
            className="altvia-content bg-white border border-gray-200 p-6 rounded-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: formattedNotes }}
          />
        </div>
      )}

      {/* Detailed Description */}
      {interactionData.detailed_description && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            Detailed Description
          </h3>
          <div className="altvia-description bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">
              {interactionData.detailed_description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AltviaInteractionViewer;
